import axios from 'axios';

interface ApiErrorBody {
  error?: string;
  description?: string;
}

/**
 * Extract the human-facing message from a backend error response. The backend
 * returns `{ error, description }` (e.g. the typed gateway-availability errors
 * like `gateway_offline` / `credential_undeliverable`), so prefer `description`,
 * then the machine `error` code, then axios' own message, then the fallback.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorBody | undefined;
    // `err.message` is always a string on an AxiosError, so it terminates the
    // chain; `fallback` only covers the non-axios branch below.
    return data?.description ?? data?.error ?? err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

/**
 * The backend `error` machine code from a typed error response, if present
 * (e.g. `gateway_offline`, `no_gateway_registered`, `credential_undeliverable`).
 * Lets the UI branch on the cause (e.g. offer a link to the gateways page).
 */
export function apiErrorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as ApiErrorBody | undefined)?.error;
  }
  return undefined;
}
